import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Modal, Table, Button } from "react-bootstrap";
import { FaChartPie, FaMoneyBillWave, FaChartBar, FaArrowDown, FaArrowUp, FaInfoCircle, FaDownload } from "react-icons/fa";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import * as XLSX from "xlsx";
import { api } from "../../config/api";
import { format, subDays } from "date-fns";
import DateRangeSelector from "./DateRangeSelector";
import SharedOrderDetailModal from "../orders/SharedOrderDetailModal";

export default function ProfitReport() {
  const [profitData, setProfitData] = useState({
    revenue: 0,
    cogs: 0,
    expenses: 0,
    netProfit: 0,
    grossProfit: 0
  });
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [loading, setLoading] = useState(false);

  // Breakdown modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'revenue', 'cogs', 'expenses'
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Order Detail modal state
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const openBreakdown = async (type) => {
    setModalType(type);
    setShowModal(true);
    setModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const qs = `?type=${type}&startDate=${dateRange.startDate} 00:00:00&endDate=${dateRange.endDate} 23:59:59`;
      const res = await api.get(`/api/reports/profit/breakdown${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModalData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setModalLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!modalData || modalData.length === 0) return;
    
    const dataForExport = modalData.map(item => {
      const row = {
        'Date': format(new Date(item.date), 'dd MMM yyyy, HH:mm'),
      };
      
      if (modalType === 'expenses') {
        row['Category'] = item.category;
        row['Description'] = item.description;
      } else {
        row['Order ID'] = item.order_id;
      }
      
      row['Amount (฿)'] = Number(item.amount);
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Breakdown");
    XLSX.writeFile(workbook, `${modalType}_breakdown_${format(new Date(), 'yyyyMMdd')}.xlsx`);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const qs = `?startDate=${dateRange.startDate} 00:00:00&endDate=${dateRange.endDate} 23:59:59`;
      const res = await api.get(`/api/reports/profit${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfitData({
        revenue: Number(res.data.revenue || 0),
        cogs: Number(res.data.cogs || 0),
        expenses: Number(res.data.expenses || 0),
        netProfit: Number(res.data.netProfit || 0),
        grossProfit: Number(res.data.grossProfit || 0)
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const pieData = [
    { name: 'Net Profit', value: profitData.netProfit > 0 ? profitData.netProfit : 0, color: '#2ecc71' },
    { name: 'Cost of Goods (COGS)', value: profitData.cogs, color: '#e74c3c' },
    { name: 'Expenses', value: profitData.expenses, color: '#f39c12' }
  ];

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0" style={{ color: "#1e3c72" }}>
          <FaChartPie className="me-2" /> Profit & Cost Report
        </h4>
        <div className="d-flex gap-2">
          <DateRangeSelector dateRange={dateRange} onChange={setDateRange} />
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col md={3}>
          <Card 
            className="border-0 shadow-sm text-center h-100" 
            style={{ borderRadius: '12px', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
            onClick={() => openBreakdown('revenue')}
          >
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2 text-muted"><FaMoneyBillWave size={24}/></div>
              <h6 className="text-uppercase mb-2 text-muted">Total Revenue <FaInfoCircle className="ms-1" size={12}/></h6>
              <h4 className="fw-bold mb-0 text-primary">฿{profitData.revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card 
            className="border-0 shadow-sm text-center h-100" 
            style={{ borderRadius: '12px', cursor: 'pointer' }}
            onClick={() => openBreakdown('cogs')}
          >
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2 text-danger"><FaArrowDown size={24}/></div>
              <h6 className="text-uppercase mb-2 text-muted">COGS (FIFO Cost) <FaInfoCircle className="ms-1" size={12}/></h6>
              <h4 className="fw-bold mb-0 text-danger">฿{profitData.cogs.toLocaleString(undefined, {minimumFractionDigits: 2})}</h4>
              <small className="text-muted mt-1">Cost of Goods Sold</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card 
            className="border-0 shadow-sm text-center h-100" 
            style={{ borderRadius: '12px', cursor: 'pointer' }}
            onClick={() => openBreakdown('expenses')}
          >
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2 text-warning"><FaArrowDown size={24}/></div>
              <h6 className="text-uppercase mb-2 text-muted">Total Expenses <FaInfoCircle className="ms-1" size={12}/></h6>
              <h4 className="fw-bold mb-0 text-warning">฿{profitData.expenses.toLocaleString(undefined, {minimumFractionDigits: 2})}</h4>
              <small className="text-muted mt-1">System & Custom Costs</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center h-100" style={{ borderRadius: '12px', background: profitData.netProfit >= 0 ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)' : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', color: 'white' }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <div className="mb-2"><FaChartBar size={24}/></div>
              <h6 className="text-uppercase mb-2 opacity-75">Net Profit</h6>
              <h3 className="fw-bold mb-0">฿{profitData.netProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <h6 className="fw-bold text-muted mb-4">Revenue Breakdown</h6>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => `฿${Number(value).toLocaleString(undefined, {minimumFractionDigits: 2})}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="d-flex flex-column justify-content-center">
              <h6 className="fw-bold text-muted mb-4">Profitability Metrics</h6>
              
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted fw-medium">Gross Margin (Revenue - COGS)</span>
                  <span className="fw-bold text-dark">
                    {profitData.revenue > 0 ? ((profitData.grossProfit / profitData.revenue) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="progress" style={{ height: '10px' }}>
                  <div className="progress-bar bg-info" role="progressbar" style={{ width: `${profitData.revenue > 0 ? (profitData.grossProfit / profitData.revenue) * 100 : 0}%` }}></div>
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted fw-medium">Net Profit Margin</span>
                  <span className="fw-bold text-dark">
                    {profitData.revenue > 0 ? ((profitData.netProfit / profitData.revenue) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="progress" style={{ height: '10px' }}>
                  <div className={`progress-bar ${profitData.netProfit >= 0 ? 'bg-success' : 'bg-danger'}`} role="progressbar" style={{ width: `${profitData.revenue > 0 ? Math.abs((profitData.netProfit / profitData.revenue) * 100) : 0}%` }}></div>
                </div>
              </div>
              
              <div className="p-3 bg-light rounded border text-muted small">
                <p className="mb-1"><strong>COGS (Cost of Goods Sold)</strong> is calculated accurately using the FIFO (First-In, First-Out) method based on your actual purchase records.</p>
                <p className="mb-0"><strong>Expenses</strong> include all system-generated costs (like stock loss) and manually recorded expenses.</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light align-items-center">
          <div className="d-flex align-items-center gap-3">
            <Modal.Title className="h5 mb-0 fw-bold text-primary">
              {modalType === 'revenue' && 'Revenue Breakdown'}
              {modalType === 'cogs' && 'COGS Breakdown'}
              {modalType === 'expenses' && 'Expenses Breakdown'}
            </Modal.Title>
            <Button 
              variant="outline-success" 
              size="sm" 
              className="d-flex align-items-center px-3 fw-medium" 
              onClick={exportToExcel} 
              disabled={modalLoading || modalData.length === 0}
            >
              <FaDownload className="me-2" /> Export Excel
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ maxHeight: '400px', overflowY: 'auto', borderBottomLeftRadius: 'calc(var(--bs-modal-border-radius, 0.5rem) - 1px)', borderBottomRightRadius: 'calc(var(--bs-modal-border-radius, 0.5rem) - 1px)' }}>
            <Table responsive hover className="align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th className="border-0 rounded-start px-4">Date</th>
                  {modalType === 'expenses' ? (
                    <>
                      <th className="border-0">Category</th>
                      <th className="border-0">Description</th>
                    </>
                  ) : (
                    <th className="border-0">Order ID</th>
                  )}
                  <th className="border-0 text-end rounded-end px-4">Amount</th>
                </tr>
              </thead>
              <tbody>
                {modalLoading ? (
                  <tr><td colSpan={modalType === 'expenses' ? 4 : 3} className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : (
                  <>
                    {modalData.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-muted px-4">{format(new Date(item.date), 'dd MMM yyyy, HH:mm')}</td>
                        {modalType === 'expenses' ? (
                          <>
                            <td><span className="badge bg-secondary">{item.category}</span></td>
                            <td>{item.description}</td>
                          </>
                        ) : (
                          <td 
                            className="fw-medium" 
                            style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }} 
                            onClick={() => { setSelectedOrderId(item.order_id); setShowOrderModal(true); }}
                          >
                            #{item.order_id}
                          </td>
                        )}
                        <td className={`text-end fw-bold px-4 ${modalType === 'revenue' ? 'text-success' : 'text-danger'}`}>
                          ฿{Number(item.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                    ))}
                    {modalData.length === 0 && <tr><td colSpan={modalType === 'expenses' ? 4 : 3} className="text-center text-muted py-4">No records found.</td></tr>}
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      <SharedOrderDetailModal 
        show={showOrderModal} 
        onHide={() => setShowOrderModal(false)} 
        orderId={selectedOrderId} 
        readOnly={true}
      />
    </Container>
  );
}
